import React, { useEffect, useState } from "react";
import {
  Box, Button, InputAdornment, Paper, TextField, Typography, Pagination, PaginationItem, Grid,
  useMediaQuery, useTheme, Dialog, Alert, Snackbar
} from "@mui/material";
import axios from "axios";
import NewCategoryPopup from "./NewCategory"
import EditCategoryPopup from "./EditCategory"
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Add01Icon, ArrowLeft01Icon, ArrowRight01Icon, Camera01Icon, Car05Icon, Chair01Icon, Delete02Icon, Dish02Icon,
  Dumbbell01Icon, Edit01Icon, FlashIcon, GeometricShapes01Icon, Motorbike02Icon, Search01Icon,
  ShoppingCart02Icon, Store01Icon, TShirtIcon, TvSmartIcon
} from "@hugeicons/core-free-icons";

const iconList = [
  { name: 'GeometricShapes01Icon', icon: GeometricShapes01Icon },
  { name: 'Motorbike02Icon', icon: Motorbike02Icon },
  { name: 'FlashIcon', icon: FlashIcon },
  { name: 'ShoppingCart02Icon', icon: ShoppingCart02Icon },
  { name: 'Dish02Icon', icon: Dish02Icon },
  { name: 'TvSmartIcon', icon: TvSmartIcon },
  { name: 'HangerIcon', icon: TShirtIcon },
  { name: 'Car05Icon', icon: Car05Icon },
  { name: 'Dumbbell01Icon', icon: Dumbbell01Icon },
  { name: 'Chair01Icon', icon: Chair01Icon },
  { name: 'Store01Icon', icon: Store01Icon },
  { name: 'Camera01Icon', icon: Camera01Icon },
];



const getCategoryIcon = (imageUrl, iconName, color) => {
  if (imageUrl) {
    const fullImageUrl = imageUrl.startsWith("http")
      ? imageUrl
      : `http://127.0.0.1:8000${imageUrl}`;

    return (
      <Box
        component="img"
        src={fullImageUrl}
        alt="Category Icon"
        sx={{ width: 24, height: 24, borderRadius: "4px" }}
      />
    );
  }

  if (iconName) {
    // Find icon object in iconList by name
    const foundIcon = iconList.find(icon => icon.name === iconName);
    if (foundIcon) {
      return <HugeiconsIcon icon={foundIcon.icon} size={24} color={color}
        style={{ fill: "currentColor", strokeWidth: 0 }}
      />;
    }
  }

  return null;
};




const CategoryTable = () => {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [open, setOpen] = useState(false);
  const [openEditPopup, setOpenEditPopup] = useState(false);
  // const [globalId, setGlobalId] = useState(0);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    type: '',
    message: '',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (event, value) => {
    setPage(value);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);


  const fetchCategories = () => {
    axios.get("http://127.0.0.1:8000/categories/list")
      .then((res) => {
        const updatedCategories = res.data.map((category) => ({
          ...category,
          catId: category.id,
        }));
        setCategories(updatedCategories);
      })
      .catch((err) => console.error("API Error:", err));
  };

  useEffect(() => {
    fetchCategories();
  }, []);


  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const filteredCategories = categories.filter((category) =>
    category.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const categoriesPerPage = 10;
  const indexOfLastCategory = page * categoriesPerPage;
  const indexOfFirstCategory = indexOfLastCategory - categoriesPerPage;
  const currentCategories = filteredCategories.slice(
    indexOfFirstCategory,
    indexOfLastCategory
  );

  const handleCardClick = (categoryId) => {
    const selected = categories.find((category) => category.id === categoryId);
    setSelectedCategory(selected);
    setSelectedIndexes([]);

    setAlertInfo({
      open: false,
      type: 'success',
      message: '',
    });
  };

  const handleCheckboxToggle = (categoryId) => {
    if (selectedIndexes.includes(categoryId)) {
      setSelectedIndexes((prev) => prev.filter((id) => id !== categoryId));
    } else {
      setSelectedIndexes((prev) => [...prev, categoryId]);
    }
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setOpenEditPopup(true);
  };

  const handleEditClose = () => {
    setOpenEditPopup(false);
    setSelectedCategory(null);
  };

  const handleDeleteCategory = async (categoryOrIds) => {
    try {
      const idsToDelete = Array.isArray(categoryOrIds)
        ? categoryOrIds
        : [categoryOrIds.catId];

      for (const id of idsToDelete) {
        await axios.delete("http://127.0.0.1:8000/categories/category-list/1", {
          data: { id },
        });
      }

      setAlertInfo({
        open: true,
        type: 'success',
        message: 'Category deleted successfully!',
      });

      fetchCategories();

      if (!Array.isArray(categoryOrIds)) {
        setSelectedCategory(null);
      }
      setSelectedIndexes([]);
    } catch (error) {
      console.error("Delete failed:", error.response?.data || error.message);
      setAlertInfo({
        open: true,
        type: 'error',
        message: 'Something went wrong during deletion!',
      });
    }
  };


  return (
    <Box sx={{ mt: 10, px: 2, minHeight: "100vh" }}>
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, background: "#fff" }}>
        {/* Top Section */}
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={2} gap={2}>
          <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} alignItems="center">
            <TextField
              placeholder="Search"
              size="small"
              fullWidth={isMobile}
              sx={{
                width: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  backgroundColor: "white",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <HugeiconsIcon icon={Search01Icon} size={20} />
                  </InputAdornment>
                ),
              }}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <Button
              startIcon={<HugeiconsIcon icon={Add01Icon} size={20} />}
              sx={{
                color: "#0b57d0",
                fontWeight: 600,
                textTransform: "none",
                fontSize: 14,
              }}
              onClick={handleOpen}
            >
              Create new category
            </Button>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              startIcon={<HugeiconsIcon icon={Delete02Icon} size={18} />}
              disabled={selectedIndexes.length === 0}
              onClick={() => handleDeleteCategory(selectedIndexes)}
              sx={{
                color: selectedIndexes.length > 0 ? "#d32f2f" : "#999",
                textTransform: "none",
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Delete All Selected
            </Button>
          </Box>
        </Box>

        {/* Category Cards */}
        <Box mt={4} p={2} sx={{ background: "#f7f7f7", borderRadius: "12px" }}>
          <Grid container spacing={2}>
            {currentCategories.map((category, index) => (
              <Grid item xs={6} sm={4} md={3} lg={2.4} key={index}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* Checkbox outside the card */}
                  <input
                    type="checkbox"
                    checked={selectedIndexes.includes(category.id)}
                    onChange={() => handleCheckboxToggle(category.id)}
                    style={{ marginRight: "8px", transform: "scale(1.2)", }}
                  />

                  {/* Card Component */}
                  <Box
                    onClick={() => handleCardClick(category.id)}
                    sx={{
                      border: selectedCategory?.id === category.id
                        ? "2px solid #0b57d0"
                        : "1px solid #ddd",
                      borderRadius: "12px",
                      p: 1.5,
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      background: selectedCategory?.id === category.id
                        ? "#e3f0ff"
                        : "#fff",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {getCategoryIcon(category.category_image, category.category_icon, category.color)}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#000",
                        textTransform: "capitalize",
                      }}
                    >
                      {category.category_name}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          {filteredCategories.length === 0 && (
            <Typography sx={{ textAlign: "center", mt: 4, color: "#999", fontSize: 14 }}>
              No categories found.
            </Typography>
          )}

          {/* New Category Popup */}
          <Dialog open={open} onClose={handleClose}>
            <NewCategoryPopup open={open} onClose={handleClose} onSuccess={fetchCategories} setAlertInfo={setAlertInfo} />
          </Dialog>
        </Box>

        {/* Pagination */}
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <Pagination
            count={Math.ceil(filteredCategories.length / categoriesPerPage)}
            page={page}
            onChange={handleChange}
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                components={{
                  previous: () => (
                    <Box sx={{ display: "flex", alignItems: "center", paddingLeft: "8px" }}>
                      <HugeiconsIcon icon={ArrowLeft01Icon} size={18} />
                      <Typography sx={{ fontSize: 14, marginLeft: "4px" }}>Previous</Typography>
                    </Box>
                  ),
                  next: () => (
                    <Box sx={{ display: "flex", alignItems: "center", paddingRight: "8px" }}>
                      <Typography sx={{ fontSize: 14, marginRight: "4px" }}>Next</Typography>
                      <HugeiconsIcon icon={ArrowRight01Icon} size={18} />
                    </Box>
                  ),
                }}
                sx={{
                  minWidth: item.type === "previous" || item.type === "next" ? "auto" : 36,
                  height: 36,
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: item.selected ? "#fff" : "#000",
                  backgroundColor: item.selected ? "#0b57d0 !important" : "#fff",
                  border: item.type === "page" ? "1px solid #D0D5DD" : "none",
                  "&:hover": {
                    backgroundColor: item.selected ? "#0b57d0" : "#f0f0f0",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.4,
                  },
                  px: item.type === "previous" || item.type === "next" ? 1.5 : 0,
                }}
              />
            )}
          />
        </Box>
      </Paper>

      {/* Category Details */}
      {selectedCategory && (
        <Paper elevation={1} sx={{ mt: 4, p: 3, borderRadius: 3, background: "#fff" }}>
          <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} mb={2} gap={2}>
            <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} alignItems="center">
              <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>Category Details</Typography>
            </Box>
            <Box display="flex" gap={2}>
              <Button
                startIcon={<HugeiconsIcon icon={Edit01Icon} size={18} />}
                onClick={() => handleEditClick(selectedCategory)}
                sx={{
                  color: "#0b57d0",
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Edit
              </Button>
              <Button
                startIcon={<HugeiconsIcon icon={Delete02Icon} size={18} />}
                onClick={() => handleDeleteCategory(selectedCategory)}
                sx={{
                  color: "#d32f2f",
                  textTransform: "none",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                Delete
              </Button>
            </Box>
          </Box>

          {/* Edit Category Popup */}
          {selectedCategory && (
            <Dialog open={openEditPopup} onClose={handleEditClose}>
              <EditCategoryPopup
                open={openEditPopup}
                onClose={handleEditClose}
                categoryData={selectedCategory}
                onSuccess={(updatedCategory) => {
                  setCategories(prev =>
                    prev.map(cat =>
                      cat.id === updatedCategory.id ? { ...cat, ...updatedCategory } : cat
                    )
                  );
                  // setTimeout(() => {
                  //   fetchCategories();
                  // }, 3000);
                  setAlertInfo({
                    open: true,
                    type: "success",
                    message: "Category updated successfully!",
                  });
                }}
                setAlertInfo={setAlertInfo}
              />
            </Dialog>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ fontSize: 14, color: "#000", fontWeight: 500 }}>
                <strong>Category Name:</strong>
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#444" }}>
                {selectedCategory.category_name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ fontSize: 14, color: "#000", fontWeight: 500 }}>
                <strong>Category Color:</strong>
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: "4px",
                    backgroundColor: selectedCategory.color,
                    border: "1px solid #ddd",
                  }}
                />
                <Typography sx={{ fontSize: 14, color: "#444" }}>{selectedCategory.color}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ fontSize: 14, color: "#000", fontWeight: 500 }}>
                <strong>Category Icon:</strong>
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {getCategoryIcon(selectedCategory.category_image, selectedCategory.category_icon, selectedCategory.color)}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
      {alertInfo.open && (
        <Snackbar
          open={alertInfo.open}
          autoHideDuration={3000}
          onClose={() => setAlertInfo({ ...alertInfo, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            variant="filled"
            severity={alertInfo.type}
            onClose={() => setAlertInfo({ ...alertInfo, open: false })}
            sx={{ width: '100%' }}
          >
            {alertInfo.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default CategoryTable;
